import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Eyebrow, Reveal } from "./Motion";
import { api } from "../lib/api";

export const Newsletter = () => {
  const { t, lang } = useLang();
  const N = t.newsletter;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const { data } = await api.post("/newsletter", { email, lang });
      setStatus(data?.already ? "already" : "ok");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };
  return (
    <section id="newsletter" data-testid="newsletter-section" className="relative py-20 lg:py-28 bg-[#16265c] text-[var(--bone)] overflow-hidden">
      <div aria-hidden="true" className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full bg-[var(--fux)] opacity-[0.14] blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute -left-32 -bottom-32 w-[380px] h-[380px] rounded-full bg-[var(--amber)] opacity-[0.10] blur-3xl pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <Eyebrow>{N.eyebrow}</Eyebrow>
          <Reveal><h2 data-testid="newsletter-title" className="mt-6 display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-[-0.01em]"><em className="serif text-[var(--amber-2)]">{N.titleAccent}</em>{N.title}</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-5 text-base lg:text-lg text-[rgba(246,241,232,0.8)] max-w-lg">{N.sub}</p></Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
          {status === "ok" || status === "already" ? (
            <motion.div data-testid={`newsletter-${status === "ok" ? "ok" : "already"}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-[rgba(246,241,232,0.25)] bg-[rgba(246,241,232,0.06)] backdrop-blur p-8 rounded-[6px]">
              <span className="w-11 h-11 rounded-full bg-[var(--fux)] text-white grid place-items-center"><Check className="w-5 h-5" /></span>
              <p className="mt-5 text-lg font-semibold leading-snug">{status === "ok" ? N.ok : N.already}</p>
              <button data-testid="newsletter-again" onClick={() => setStatus("idle")} className="mt-5 mono text-[12px] uppercase text-[var(--amber-2)] link-sweep">↺ {N.cta}</button>
            </motion.div>
          ) : (
            <form data-testid="newsletter-form" onSubmit={submit} className="border border-[rgba(246,241,232,0.25)] bg-[rgba(246,241,232,0.06)] backdrop-blur p-8 rounded-[6px]">
              <label className="block">
                <span className="mono text-[11px] uppercase text-[rgba(246,241,232,0.75)]">{N.placeholder}</span>
                <input data-testid="newsletter-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={N.placeholder} className="field" />
              </label>
              <button data-testid="newsletter-submit" type="submit" disabled={status === "sending"}
                className="group mt-7 inline-flex items-center gap-3 rounded-full bg-[var(--fux)] text-white pl-7 pr-2 py-2 font-semibold hover:bg-[var(--amber)] hover:text-[var(--ink)] transition-colors duration-300 disabled:opacity-70">
                {status === "sending" ? N.sending : N.cta}
                <span className="w-10 h-10 rounded-full bg-white/15 text-white grid place-items-center">
                  {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />}
                </span>
              </button>
              {status === "error" && <p data-testid="newsletter-error" className="text-sm text-red-300 mt-4">{N.error}</p>}
              <p data-testid="newsletter-privacy" className="mono text-[10.5px] uppercase leading-5 text-[rgba(246,241,232,0.55)] mt-6">{N.privacy}</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};